// Presentation-only color system for sociopath-test-report.
//
// No scoring/business logic lives here — these functions only ever consume
// an already-normalized 0-100 value or an already-computed classification
// band already present on `reportData` (or a Dev Controls override layered
// on top of it, see dev-tools/report-debug-panel.js). They decide paint,
// nothing else.
//
// Two color strategies coexist by design, not oversight: FactorCard colors
// below ramp intensity with percentage within a fixed hue per factor
// (purple/red/teal/gray). The overall gauge and severity-band colors
// (further below) are fixed, flat colors per classification band instead —
// no percentage-driven intensity.

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const lerp = (start, end, t) => start + (end - start) * t;
const mix = ([start, end], t) => lerp(start, end, t);
const hsl = (hue, saturation, lightness) => `hsl(${hue}, ${saturation}%, ${lightness}%)`;

// ---------------------------------------------------------------------
// Per-factor cards — same hue per factor as the original
// getTailwindColorClasses mapping (purple/red/teal/gray), so each factor
// keeps its own identity; only intensity/lightness moves with that
// factor's own percentage.
// ---------------------------------------------------------------------
const FACTOR_STYLES = {
  purple: {
    hue: 271,
    bg: { s: [92, 100], l: [99, 85] }, // ~ purple-200 at 50%
    text: { s: [52, 82], l: [50, 28] }, // ~ purple-800 at 50%
    bar: { s: [66, 96], l: [71, 41] }, // ~ purple-600 at 50%
  },
  red: {
    hue: 0,
    bg: { s: [88, 100], l: [96, 82] }, // ~ red-200 at 50%
    text: { s: [57, 87], l: [46, 24] }, // ~ red-800 at 50%
    bar: { s: [57, 87], l: [66, 36] }, // ~ red-600 at 50%
  },
  teal: {
    hue: 172,
    bg: { s: [76, 92], l: [85, 71] }, // ~ teal-200 at 50%
    text: { s: [54, 84], l: [33, 11] }, // ~ teal-800 at 50%
    bar: { s: [69, 99], l: [47, 17] }, // ~ teal-600 at 50%
  },
  gray: {
    hue: 220,
    bg: { s: [5, 21], l: [98, 84] }, // ~ gray-200 at 50%
    text: { s: [4, 34], l: [38, 16] }, // ~ gray-700 at 50%
    bar: { s: [0, 29], l: [49, 19] }, // ~ gray-600 at 50%
  },
};

export const getFactorVisualStyle = (colorName, percentage) => {
  const style = FACTOR_STYLES[colorName] || FACTOR_STYLES.gray;
  const t = clamp(percentage, 0, 100) / 100;
  const paint = ({ s, l }) => hsl(style.hue, mix(s, t), mix(l, t));

  return {
    bgColor: paint(style.bg),
    textColor: paint(style.text),
    barColor: paint(style.bar),
  };
};

// ---------------------------------------------------------------------
// Overall gauge — one fixed, flat strong color per band, no gradient, no
// percentage-driven intensity. A pure lookup keyed by the already-computed
// classification band (reportData.overallResult.band) — no percentage/
// threshold logic here, so this can never disagree with the classification
// decided elsewhere.
// ---------------------------------------------------------------------
const SEVERITY_GAUGE_COLORS = {
  none: { color: "#37C978", textColor: "#249D5C" },
  mild: { color: "#F6C343", textColor: "#D89E00" },
  moderate: { color: "#E0791F", textColor: "#E0791F" },
  severe: { color: "#F25555", textColor: "#E53935" },
};

export const getSeverityGaugeVisualStyle = (band) => {
  const palette = SEVERITY_GAUGE_COLORS[band] || SEVERITY_GAUGE_COLORS.none;
  return {
    // Flat solid stroke — both gradient stops are the same color, so
    // OverallResultGauge (unmodified) renders a single flat fill instead
    // of a gradient.
    gradientFrom: palette.color,
    gradientTo: palette.color,
    textColor: palette.textColor,
  };
};

// ---------------------------------------------------------------------
// Scores table values, the classification row/card, and the label under
// the gauge — fixed severity-family colors: ONE strong flat color per band
// (reused verbatim from SEVERITY_GAUGE_COLORS above, so the gauge and this
// styling can never disagree), a very light background tint, and a subtle
// matching border. No percentage input, no intensity ramp — `band` is
// always supplied by the caller from already-computed classification data
// (reportData.factors[].level / reportData.overallResult.band) and is the
// sole source of color; this function cannot pick a different band than
// the one it's given.
//
// bg/border for green/yellow/red are Tailwind's own 50/200 steps; moderate/
// orange intentionally uses custom hex (#fdf3e6/#f3ddb8) instead — matches
// this report's existing design, not a Tailwind step.
// ---------------------------------------------------------------------
const SEVERITY_BAND_COLORS = {
  none: {
    strong: SEVERITY_GAUGE_COLORS.none.textColor, // #249D5C
    bg: "#F0FDF4", // Tailwind green-50
    border: "#BBF7D0", // Tailwind green-200
  },
  mild: {
    strong: SEVERITY_GAUGE_COLORS.mild.textColor, // #D89E00
    bg: "#FEFCE8", // Tailwind yellow-50
    border: "#FEF08A", // Tailwind yellow-200
  },
  moderate: {
    strong: SEVERITY_GAUGE_COLORS.moderate.textColor, // #E0791F
    bg: "#FDF3E6",
    border: "#F3DDB8",
  },
  severe: {
    strong: SEVERITY_GAUGE_COLORS.severe.textColor, // #E53935
    bg: "#FEF2F2", // Tailwind red-50
    border: "#FECACA", // Tailwind red-200
  },
};

export const getSeverityBandColors = (band) =>
  SEVERITY_BAND_COLORS[band] || SEVERITY_BAND_COLORS.none;
